import React, { useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Cookies from "universal-cookie";
import moment from "moment";
import i18n from "../i18n";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import {
  registerEnrollee,
  validateEnrollmentCode,
  saveEnrollee,
  clientCheck,
  loadEnrollee,
} from "../_redux/actions/enrolleeActions";

import {
  LifeSafer,
  Monitech,
  Guardian,
  QuickStart,
} from "../_helpers/BrandConstants";
import { PageLoader } from "@/_components";
import { USA, Australia } from "../_helpers/CountryConstants";
import { getEnrollmentCodeStateDefaultByType } from "../_api/enrolleeApi";

import favIconLogo from "../_images/favicons/ls_favicon.png";
import favIconMonLogo from "../_images/favicons/mon_favicon.png";
import favIconGuardLogo from "../_images/favicons/guard_favicon.png";
import favIconQsLogo from "../_images/favicons/qs_favicon.png";
import { utilities, langConversionHelper } from "@/_helpers";

const Entry = ({
  registerEnrollee,
  validateEnrollmentCode,
  saveEnrollee,
  clientCheck,
  loadEnrollee,
}) => {
  const navigate = useNavigate();
  const { enrollmentCodeParam } = useParams();
  const { pathname } = useLocation();
  const url = window.location.href;
  const cookies = new Cookies();
  const brand = cookies.get("lsEnrollmentReferral") || LifeSafer;
  const normalizedUrl = url.toLowerCase();
  const isISA = normalizedUrl.includes("lifesaferisa");
  const isEuropeEnrollmentSite =
    window.location.hostname.toLowerCase().endsWith(".eu") ||
    String(process.env.REACT_APP_ENV || "")
      .toLowerCase()
      .startsWith("eu");

  const brandList = [
    {
      id: 1,
      Name: "LifeSafer",
      BrandNameInitial: "lifeSafer",
      BrandIdInitial: 1,
    },
    {
      id: 2,
      Name: "Guardian",
      BrandNameInitial: "guardianinterlock",
      BrandIdInitial: 2,
    },
    {
      id: 3,
      Name: "Monitech",
      BrandNameInitial: "monitechiid",
      BrandIdInitial: 3,
    },
    {
      id: 4,
      Name: "QuickStart",
      BrandNameInitial: "quickstartaz",
      BrandIdInitial: 3,
    },
  ];

  const mailBrand = brandList.find(
    (brandData) =>
      brandData?.BrandNameInitial?.toLowerCase() === brand?.toLowerCase() ||
      brandData?.Name?.toLowerCase() === brand?.toLowerCase(),
  );
  const customEmail = mailBrand?.Name.toLowerCase();

  const resolveClientDeviceType = (clientTypeId) => {
    if (clientTypeId === 5) return "Portable";
    if (clientTypeId === 6) return "Speed";
    if (clientTypeId === 1) return "Interlock";
    return null;
  };

  const normalizeEnrolleeDeviceType = (enrolleeData) => {
    if (!enrolleeData) return enrolleeData;

    const clientTypeDevice = resolveClientDeviceType(
      enrolleeData?.clientTypeId ?? enrolleeData?.ClientTypeId,
    );

    const normalizedDevice =
      clientTypeDevice ||
      utilities?.deviceInfo(enrolleeData?.deviceType)?.deviceType ||
      null;

    if (!normalizedDevice) return enrolleeData;

    return {
      ...enrolleeData,
      deviceType: normalizedDevice,
    };
  };

  useEffect(() => {
    handleReferringSite(window.location.href);
    handleLanguageAndEntryCode(pathname);
    handleCountry(window.location);
  }, [pathname]);

  // Centralized enrollment resolver - Ankit
  async function resolveEnrollmentCode({
    enrollmentCodeParam,
    CookieEnrollmentCode,
    State,
    brand,
    cookies,
  }) {
    let finalEnrollmentCodeId = null;

    // 1. Parameter
    if (enrollmentCodeParam) {
      try {
        const result = await validateEnrollmentCode(enrollmentCodeParam, brand);
        if (result?.enrollmentCodeId) {
          if (result?.languageId) {
            applyEnrollmentLanguage(result.languageId);
          }
          finalEnrollmentCodeId = result;
          cookies.set("lsEnrollmentCode", result, {
            path: "/",
            sameSite: "strict",
          });
          return finalEnrollmentCodeId;
        }
      } catch (err) {
        console.error("Param enrollment code failed", err);
      }
    }

    // 2. Cookie
    if (CookieEnrollmentCode) {
      try {
        const result = await validateEnrollmentCode(
          CookieEnrollmentCode,
          brand,
        );
        if (result?.enrollmentCodeId) {
          if (result?.languageId) {
            applyEnrollmentLanguage(result.languageId);
          }
          finalEnrollmentCodeId = result;
          cookies.set("lsEnrollmentCode", result, {
            path: "/",
            sameSite: "strict",
          });
          return finalEnrollmentCodeId;
        }
      } catch (err) {
        console.error("Cookie enrollment code failed", err);
      }
    }

    // 3. State
    if (State) {
      try {
        const result = await getEnrollmentCodeStateDefaultByType(
          State,
          "Form Lead",
          brand,
        );
        if (result?.enrollmentCodeId) {
          if (result?.languageId) {
            applyEnrollmentLanguage(result.languageId);
          }
          finalEnrollmentCodeId = result;
          cookies.set("lsEnrollmentCode", result, {
            path: "/",
            sameSite: "strict",
          });

          return finalEnrollmentCodeId;
        }
      } catch (err) {
        console.error("State enrollment code failed", err);
      }
    }

    // 4. Absolute Default (LifeSafer's)
    try {
      const result = await validateEnrollmentCode("LIFESAFERDEFAULT", brand);
      if (result?.enrollmentCodeId) {
        if (result?.languageId) {
          applyEnrollmentLanguage(result.languageId);
        }
        finalEnrollmentCodeId = result;
        cookies.set("lsEnrollmentCode", result, {
          path: "/",
          sameSite: "strict",
        });
      }
    } catch (err) {
      console.error("Default enrollment code failed", err);
    }

    return finalEnrollmentCodeId;
  }

  function applyEnrollmentLanguage(languageId) {
    const enrollNow = cookies.get("EnrollNow") || {};
    const cookieLanguage =
      enrollNow?.LanguageId ??
      enrollNow?.languageId ??
      enrollNow?.LanguageID ??
      enrollNow?.languageID ??
      enrollNow?.Language ??
      enrollNow?.language;
    const supportedLangs = langConversionHelper.languageList();
    const lang = [cookieLanguage, languageId]
      .map((preferredLanguage) => {
        const normalizedPreferredLanguage = String(
          preferredLanguage ?? "",
        ).toLowerCase();
        const matchedLang = supportedLangs.find(
          (x) =>
            x.langAbbrev.toLowerCase() === normalizedPreferredLanguage ||
            x.nativeName.toLowerCase() === normalizedPreferredLanguage ||
            x.displayName.toLowerCase() === normalizedPreferredLanguage ||
            String(x.languageId ?? x.languageID) ===
              normalizedPreferredLanguage,
        );
        const matchedLanguageCode =
          (matchedLang?.languageId ?? matchedLang?.languageID) === 1
            ? "enUS"
            : matchedLang?.langAbbrev;
        return matchedLanguageCode &&
          i18n.hasResourceBundle(matchedLanguageCode, "translation")
          ? matchedLanguageCode
          : null;
      })
      .find(Boolean) || "enUS";

    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
  }

  useEffect(() => {
    const lifeSaferUser = cookies.get("EnrollNow") || {};
    const {
      FirstName,
      LastName,
      Email,
      CellPhone,
      State,
      DeviceType,
      Language,
      Story,
      EnrollmentCode: CookieEnrollmentCode,
      URL: Url,
      Page:rawPage,
      ProgramType,
    } = lifeSaferUser;
    const enrollmentCode = enrollmentCodeParam || CookieEnrollmentCode;
    const Page = rawPage?.substring(0, 150) || "";

    const fallbackEmail =
      Email || (CellPhone ? `${CellPhone}@${customEmail}.com` : "");
    const canClientCheck = FirstName && LastName && CellPhone;
    const canRegister = FirstName && LastName && CellPhone && State;

    const {
      UTM_source,
      UTM_medium,
      UTM_campaign,
      GA_clientid,
      gclid,
      LocationId,
      ShowLPP,
      UserAgent,
      IPAddress,
      wbraid,
      gbraid,
      fbclid,
      fbp,
      ttclid,
      rdt_cid,
    } = lifeSaferUser;

    async function startEntryFlow() {
      try {
        const validStories = [
          "pricing",
          "schedule",
          "location",
          "guidance",
          "speed",
          "isa volunteer",
          "schedule now",
          "partner",
          "europe",
        ];
        const getStoryRoute = (story) => {
          const storyLower = String(story || "")
            .trim()
            .toLowerCase();

          if (!validStories.includes(storyLower)) {
            return null;
          }

          if (storyLower === "isa volunteer") {
            return "/story/speed";
          }

          if (storyLower === "schedule now") {
            return "/story/schedule";
          }

          return `/story/${storyLower}`;
        };
        const getPathStoryRoute = () => {
          const normalizedPathname = pathname.toLowerCase();
          if (!normalizedPathname.startsWith("/story/")) return "/pick-a-story";
          const storyFromPath = normalizedPathname.split("/")[2];
          return validStories.includes(storyFromPath)
            ? `/story/${storyFromPath}`
            : "/pick-a-story";
        };

        if (canClientCheck) {
          try {
            const clientCheckData = await clientCheck({
              firstName: FirstName,
              lastName: LastName,
              cellPhone: CellPhone,
              email: fallbackEmail,
              brandIdentifier: brand || LifeSafer,
            });
            if (
              ["Installed", "Scheduled"].includes(clientCheckData?.clientStatus)
            ) {
              return navigate("/my-account", { state: { clientCheckData } });
            }
          } catch (err) {
            console.error("clientCheck failed:", err);
          }
        }

        if (canRegister) {
          let enrollee = null;
          const finalEnrollmentCodeId = await resolveEnrollmentCode({
            enrollmentCodeParam,
            CookieEnrollmentCode,
            State,
            brand: brand || LifeSafer,
            cookies,
          });
          let finalStory =
            Story ||
            finalEnrollmentCodeId?.story ||
            finalEnrollmentCodeId?.Story ||
            null;
          if (finalStory) {
            const storyLower = String(finalStory).toLowerCase();
            if (storyLower === "speed") {
              finalStory = "ISA Volunteer"; 
            } else if (storyLower === "schedule") {
              finalStory = "Schedule Now";
            }
          }
          try {
            enrollee = await registerEnrollee({
              EnrollmentCodeId: finalEnrollmentCodeId?.enrollmentCodeId || null,
              BrandIdentifier: cookies.get("lsEnrollmentReferral") || LifeSafer,
              FirstName,
              LastName,
              CellPhone,
              HomePhone: CellPhone,
              DeviceType:
                finalStory?.toLowerCase() === "isa volunteer" ||
                isISA ||
                finalStory?.toLowerCase() === "speed"
                  ? "Speed"
                  : utilities?.deviceInfo(DeviceType)?.deviceType ||
                    "Interlock",
              Email: fallbackEmail,
              Step: "Entry",
              LinkTypeID: cookies.get("lslinkTypeID") || null,
              LinkID: cookies.get("lslinkID") || null,
              WebUserName: null,
              Page: Page || null,
              ReferURL: Url?.slice(0, 500) || null,
              Story: finalStory || null,
              ContactPreference: Email ? "Text" : "Phone",
              utM_source: UTM_source || null,
              utM_medium: UTM_medium || null,
              utM_campaign: UTM_campaign || null,
              gA_clientid: GA_clientid || null,
              gclID: gclid || null,
              LocationId: LocationId,
              userAgent: UserAgent || null,
              ipAddress: IPAddress || null,
              wbraid: wbraid || null,
              gbraid: gbraid || null,
              fbclid: fbclid || null,
              fbp: fbp || null,
              ttclid: ttclid || null,
              rdt_cid: rdt_cid || null,
              protPlan: ShowLPP || null,
            });
            enrollee = normalizeEnrolleeDeviceType(enrollee);
          } catch (error) {
            console.error("Error getting enrollment:", error);
          }

          if (!enrollee?.jwtToken) {
            sessionStorage.setItem("enrolleeUser", JSON.stringify(enrollee));
            if (["TEXT", "EMAIL"].includes(enrollee?.resetSent)) {
              return navigate("/verify", {
                state: { enrollee, lifeSaferUser },
              });
            }
            if (enrollee?.resetSent === "CALL") {
              return navigate("/contact-page");
            }
          }

          if (enrollee?.jwtToken) {
            cookies.set(
              "lifesaferEnrollment",
              JSON.stringify(normalizeEnrolleeDeviceType(enrollee)),
              {
                path: "/",
                sameSite: "strict",
              },
            );

            if (FirstName && LastName && CellPhone && State) {
              const enrolleeClientdata = {
                enrolleeId: enrollee?.enrolleeId,
                userId: enrollee?.userId,
                firstName: FirstName || null,
                lastName: LastName || null,
                state: State || null,
                dob: null,
                homeAddr1: null,
                homeAddr2: null,
                city: null,
                zip: null,
                county: null,
                country: null,
                dlState: null,
                dlNum: "CE - NEED",
                workPhone: null,
                programType: ProgramType || null,
                protPlan: ShowLPP || null,
                dlExpirationDate: null,
                email: fallbackEmail || null,
                homePhone: CellPhone || null,
                cellPhone: CellPhone || null,
                clientId: enrollee?.clientId || 0,
                languageID:
                  utilities?.getLanguageId(
                    localStorage?.getItem("lang") || Language,
                  ) || 1,
              };
              try {
                const clientResponse = await saveEnrollee(enrolleeClientdata);
                if (clientResponse?.clientId && clientResponse?.userId) {
                  if (isEuropeEnrollmentSite) {
                    const europeEnrollee = normalizeEnrolleeDeviceType({
                      ...enrollee,
                      ...clientResponse,
                      enrolleeId:
                        clientResponse.enrolleeId || enrollee.enrolleeId,
                      userId: clientResponse.userId || enrollee.userId,
                      clientId: clientResponse.clientId,
                    });

                    sessionStorage.setItem(
                      "enrolleeUser",
                      JSON.stringify(europeEnrollee),
                    );
                    cookies.set(
                      "lifesaferEnrollment",
                      JSON.stringify(europeEnrollee),
                      {
                        path: "/",
                        sameSite: "strict",
                      },
                    );
                  }

                  const enrolleeData = await loadEnrollee(
                    clientResponse.clientId,
                    clientResponse.userId,
                  );
                  if (enrolleeData) {
                    sessionStorage.setItem(
                      "enrolleeUser",
                      JSON.stringify(normalizeEnrolleeDeviceType(enrolleeData)),
                    );
                  }
                }
              } catch (err) {
                console.error("loadEnrollee failed:", err);
              }
            }
          }
        }

        // Story & Enrollment Navigation Handler
        const finalEnrollmentCodeId = await resolveEnrollmentCode({
          enrollmentCodeParam,
          CookieEnrollmentCode,
          State,
          brand: brand || LifeSafer,
          cookies,
        });
        const storyRoute = getStoryRoute(Story);
        if (isEuropeEnrollmentSite) {
          return navigate("/story/europe", { replace: true });
        }
        if (Story) {
          if (storyRoute) return navigate(storyRoute);
          return navigate("/pick-a-story");
        }

        const enrollmentCodeStoryRoute = getStoryRoute(
          finalEnrollmentCodeId?.story || finalEnrollmentCodeId?.Story,
        );
        if (enrollmentCodeStoryRoute) {
          return navigate(enrollmentCodeStoryRoute);
        }

        if (enrollmentCode) {
          const result = await validateEnrollmentCode(
            finalEnrollmentCodeId?.enrollmentCode || enrollmentCode,
            brand || LifeSafer,
          );
          const resultStoryRoute = getStoryRoute(
            result?.story || result?.Story,
          );
          if (resultStoryRoute) {
            return navigate(resultStoryRoute);
          }

          const pathStoryRoute = getPathStoryRoute();
          if (!result?.story && pathStoryRoute) {
            return navigate(pathStoryRoute, { replace: true });
          }
        }

        const pathStoryRoute = getPathStoryRoute();
        if (pathStoryRoute) {
          return navigate(pathStoryRoute, { replace: true });
        }

        if (!enrollmentCode) {
          return navigate("/", { replace: true });
        }

        return navigate("/pick-a-story");
      } catch (err) {
        console.error("Error in entry flow:", err);
        return navigate("/pick-a-story");
      }
    }

    startEntryFlow();
  }, [navigate, enrollmentCodeParam, pathname]);

  function handleReferringSite(url = "") {
    const ref = url.toLowerCase();
    const faviconEl = document.getElementById("favicon");
    const setBrand = (brand, title, icon) => {
      document.title = title;
      const exp = moment().add(24, "hours").toDate();
      if (faviconEl) faviconEl.href = icon;
      cookies.set("lsEnrollmentReferral", brand, {
        path: "/",
        sameSite: "strict",
        expires: exp,
      });
      cookies.set("lsBackgroundImage", "", {
        path: "/",
        sameSite: "strict",
        expires: exp,
      });
    };

    if (ref.includes(LifeSafer.toLowerCase()))
      setBrand(LifeSafer, "LifeSafer - Enrollment", favIconLogo);
    else if (ref.includes(Monitech.toLowerCase()))
      setBrand(Monitech, "Monitech - Enrollment", favIconMonLogo);
    else if (ref.includes(Guardian.toLowerCase()))
      setBrand(Guardian, "Guardian - Enrollment", favIconGuardLogo);
    else if (ref.includes(QuickStart.toLowerCase()))
      setBrand(QuickStart, "QuickStart - Enrollment", favIconQsLogo);
    else setBrand(LifeSafer, "LifeSafer - Enrollment", favIconLogo);
  }

  function handleLanguageAndEntryCode(pathname) {
    if (pathname.startsWith("/intro")) {
      let lang = "enUS";
      const urlParams = new URLSearchParams(window.location.search);
      const langParam = urlParams.get("lang");
      if (langParam) lang = langParam;

      const signifier = pathname.split("/").pop();
      if (signifier?.length === 2) {
        lang = signifier;
      } else {
        localStorage.setItem("page", "Enrollment Code");
        cookies.set("lsEnrollmentCode", signifier, {
          path: "/",
          sameSite: "strict",
          expires: moment().add(2, "minutes").toDate(),
        });
      }

      localStorage.setItem("lang", lang);
      i18n.changeLanguage(lang);
    }

    if (pathname.startsWith("/entry") && pathname.length > 6) {
      const signifier = pathname.split("/").pop().toUpperCase();
      const types = ["TEST", "MAILER", "DUISOLUTIONS"];
      if (types.includes(signifier)) {
        cookies.set("lsEnrollmentEntryType", signifier, {
          path: "/",
          sameSite: "strict",
          expires: moment().add(20, "minutes").toDate(),
        });
      } else {
        cookies.remove("lsEnrollmentEntryType", { path: "/" });
      }
    }
  }

  function handleCountry(siteUrl) {
    const storedLang = localStorage.getItem("lang");
    if (storedLang) return;
    const host = siteUrl.hostname.toLowerCase();
    const isAU =
      host.includes(".au") ||
      ["ausdev", "austest", "ausprod"].includes(process.env.REACT_APP_ENV);
    const exp = moment().add(24, "hours").toDate();

    if (isAU) {
      cookies.set("siteCountry", Australia, {
        path: "/",
        sameSite: "strict",
        expires: exp,
      });
      if (["enUS", "en-US", "en"].includes(i18n.language))
        i18n.changeLanguage("enAU");
    } else {
      cookies.set("siteCountry", USA, {
        path: "/",
        sameSite: "strict",
        expires: exp,
      });
      if (["enAU", "en-AU"].includes(i18n.language))
        i18n.changeLanguage("enUS");
    }
  }

  return (
    <Grid
      container
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginTop: "100px",
        width: "100%",
      }}
    >
      <Grid>
        <PageLoader />
      </Grid>
    </Grid>
  );
};

Entry.propTypes = {
  registerEnrollee: PropTypes.func.isRequired,
  validateEnrollmentCode: PropTypes.func.isRequired,
  saveEnrollee: PropTypes.func.isRequired,
  clientCheck: PropTypes.func.isRequired,
  loadEnrollee: PropTypes.func,
};

const mapDispatchToProps = {
  clientCheck,
  registerEnrollee,
  validateEnrollmentCode,
  saveEnrollee,
  loadEnrollee,
};

export default connect(null, mapDispatchToProps)(Entry);
